import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Customer } from '@pos-app/data';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { CoreUiPartialState } from '../../+state/core-ui/core-ui.reducer';
import { ApiService } from '../../services';
import {
  clearshipToSearchData,
  searchshipToCustomers,
} from '../../+state/core-ui/core-ui.actions';
import {
  getHasMoreRecordsShipTo,
  getSearchingShipToCustomer,
  getShipToCustomers,
} from '../../+state/core-ui/core-ui.selectors';

@Component({
    selector: 'pos-app-lib-customer-ship-to-search',
    templateUrl: './customer-ship-to-search.component.html',
    styleUrls: ['./customer-ship-to-search.component.scss'],
    standalone: false
})
export class CustomersShipToSearchComponent
  implements OnInit, OnDestroy, OnChanges
{
  @ViewChild('searchBox') searchBox: ElementRef;

  @Input() forOrderSearch: boolean;
  @Input() forOrderHeader: boolean;
  @Input() selectedCustomerName: string;
  @Input() disabled: boolean;
  @Input() removeCustomerAfterLoad = false;
  @Input() placeHolder: string;
  @Input() showSearchIcon: boolean;

  @Output() selectNewShipToCustomer = new EventEmitter<Customer>();

  private unSubscribe$ = new Subject<void>();
  loading$: Observable<boolean>;
  shipToCustomer$: Observable<Customer[]>;
  hasMoreRecords$: Observable<boolean>;
  hasMoreRecords = false;
  shipToCustomerSelected: Customer = null;
  listOfCustomers: Customer[] = [];
  readonly defaultPlaceHolder = 'Search Ship To Customer';

  indexFocus = 0;
  show = false;
  shipToCustomerSearchForm: UntypedFormGroup;
  selectType = false;

  constructor(
    private coreUIStore: Store<CoreUiPartialState>,
    private fb: UntypedFormBuilder,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.placeHolder = this.placeHolder || this.defaultPlaceHolder;
    this.initForm();
    this.initSubscriptions();
    this.onFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCustomerName && this.shipToCustomerSearchForm) {
      this.shipToCustomerSearchForm.patchValue(
        {
          shipToCustomerSearch: this.removeCustomerAfterLoad
            ? ''
            : this.selectedCustomerName,
        },
        { emitEvent: false }
      );
    }

    if (changes.disabled && this.shipToCustomerSearchForm) {
      const control = this.shipToCustomerSearchForm.get('shipToCustomerSearch');
      if (this.disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
        this.searchBox?.nativeElement.focus();
      }
    }
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private initForm() {
    this.shipToCustomerSearchForm = this.fb.group({
      shipToCustomerSearch: [{ value: '', disabled: this.disabled }],
      customerSearchType: [''],
    });
  }

  private initSubscriptions() {
    this.coreUIStore
      .select(getShipToCustomers)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((customers) => {
        this.listOfCustomers = customers;
        const match = customers.find(
          (c) =>
            c.customerNumber ===
            this.shipToCustomerSearchForm.get('shipToCustomerSearch').value
        );
        if (match) {
          this.selectCustomer(match);
        } else {
          this.shipToCustomer$ = this.coreUIStore.pipe(select(getShipToCustomers));
        }
      });

    this.loading$ = this.coreUIStore.select(getSearchingShipToCustomer);
    this.hasMoreRecords$ = this.coreUIStore.select(getHasMoreRecordsShipTo);

    this.hasMoreRecords$
      ?.pipe(takeUntil(this.unSubscribe$))
      .subscribe((hasMore) => (this.hasMoreRecords = hasMore));
  }

  private onFormChanges() {
    this.shipToCustomerSearchForm
      .get('shipToCustomerSearch')
      .valueChanges.pipe(
        tap(() => {
          this.resetSelection();
        }),
        debounceTime(500),
        distinctUntilChanged(),
        filter((v) => (v || '').trim().length >= 3),
        takeUntil(this.unSubscribe$)
      )
      .subscribe((value: string) => {
        this.coreUIStore.dispatch(clearshipToSearchData());
        this.coreUIStore.dispatch(
          searchshipToCustomers({
            customerNumber: 0,
            customerfuzzySearch: value.replace('-', ''),
            customerType: '',
          })
        );
      });

    this.shipToCustomerSearchForm
      .get('customerSearchType')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((value) => {
        this.coreUIStore.dispatch(
          searchshipToCustomers({
            customerNumber: 0,
            customerfuzzySearch: value.replace('-', ''),
            customerType: '',
          })
        );
        this.selectType = true;
      });

    
  }

  private resetSelection() {
    this.shipToCustomerSelected = null;
    this.listOfCustomers = [];
    this.hasMoreRecords = false;
    this.selectNewShipToCustomer.emit(null);
    this.shipToCustomerSearchForm.patchValue(
      { customerSearchType: '' },
      { emitEvent: false }
    );
  }

  onKeyboard(event: KeyboardEvent) {
    if (!this.listOfCustomers?.length) return;

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.indexFocus = Math.max(
        0,
        (this.indexFocus + delta) % this.listOfCustomers.length
      );
    } else if (event.key === 'Enter') {
      this.selectCustomer(this.listOfCustomers[this.indexFocus]);
    } else {
      this.show = true;
    }
  }

  lostFocus(e) {
    setTimeout(() => {
      this.show = false;
      if (this.selectType) {
        this.searchBox?.nativeElement.focus();
        this.selectType = false;
      }
    }, 300);
  }

  selectCustomer(customer: Customer) {
    if (!customer) return;
    this.show = false;
    this.shipToCustomerSelected = customer;
    this.selectNewShipToCustomer.emit(customer);
    this.shipToCustomerSearchForm.patchValue(
      { shipToCustomerSearch: customer.customerName },
      { emitEvent: false }
    );
    this.coreUIStore.dispatch(clearshipToSearchData());
  }

  getLocation(customer: Customer): string {
    const parts = [
      customer.suburb,
      customer.state,
      customer.countryName,
    ].filter(Boolean);
    return parts.join(', ');
  }

  isDisabled(): boolean {
    return this.disabled;
  }

  isFocus(event: FocusEvent) {
    this.show = true;
    (event.target as HTMLInputElement).select();
  }
}

